/* eslint-disable check-file/filename-blocklist -- pre-existing .jsx; this change only swaps an icon import, converting to .tsx is a separate migration */
import React, { useEffect, useState } from 'react';
import { Flex, Box, Icon, Link, Text, Tooltip } from '@chakra-ui/react';
import { formatBytes } from '@/utils/format-bytes';
import { useField } from 'formik';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import AttachmentDoc from '@/assets/icons/attachements/AttachmentDoc';
import AttachmentPdf from '@/assets/icons/attachements/AttachmentPdf';
import AttachmentDocx from '@/assets/icons/attachements/AttachmentDocx';
import AttachmentJpg from '@/assets/icons/attachements/AttachmentJpg';
import AttachmentOdt from '@/assets/icons/attachements/AttachmentOdt';
import AttachmentPng from '@/assets/icons/attachements/AttachmentPng';
import AttachmentDefault from '@/assets/icons/attachements/AttachmentDefault';
import { InfoCircle, X } from '@kula-ai/icons-core';

export const FileAttachmentPreview = ({
  file,
  attachmentKey = 'attachments',
  onAttachmentRemove: onAttachmentRemoveCallback,
}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [fileObj, setFileObj] = useState(null);
  const [field, _meta, helpers] = useField({ name: attachmentKey }); // eslint-disable-line
  // eslint-disable-next-line
  const [_hiddenField, _hiddenMeta, hiddenHelpers] = useField({
    name: `${attachmentKey}_updated`,
  });

  const fileConfigs = [
    {
      type: 'application/pdf',
      icon: AttachmentPdf,
    },
    {
      type: 'application/msword',
      icon: AttachmentDoc,
    },
    {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      icon: AttachmentDocx,
    },
    {
      type: 'image/jpeg',
      icon: AttachmentJpg,
    },
    {
      type: 'image/jpg',
      icon: AttachmentJpg,
    },
    {
      type: 'image/png',
      icon: AttachmentPng,
    },
    {
      type: 'application/vnd.oasis.opendocument.text',
      icon: AttachmentOdt,
    },
    // Default fallback
    {
      type: 'default',
      icon: AttachmentDefault,
    },
  ];

  const getFileIcon = (fileType) => {
    const config = fileConfigs.find((config) => config.type === fileType);
    return config ? config.icon : AttachmentDefault;
  };

  useEffect(() => {
    if (file instanceof File) {
      setFileUrl(URL.createObjectURL(file));
      setFileObj(file);
    } else {
      setFileUrl(file?.url);
      setFileObj({
        id: file?.id,
        url: file?.url,
        name: file?.filename,
        size: file?.byte_size,
        type: file?.content_type,
        isNonRemovable: file?.isNonRemovable || false,
        hasError: file?.hasError || false,
      });
    }
  }, [file]);

  const onAttachmentRemove = () => {
    hiddenHelpers.setValue(true);
    if (file instanceof File)
      helpers.setValue(field.value.filter((fi) => fi !== file));
    else {
      helpers.setValue(field.value.filter((fi) => fi?.id !== file?.id));
    }

    // Call the callback if provided
    if (
      onAttachmentRemoveCallback &&
      typeof onAttachmentRemoveCallback === 'function'
    ) {
      onAttachmentRemoveCallback(file);
    }
  };

  if (!fileUrl) return null;
  return (
    <Flex
      minW="236px"
      bg="rgba(229, 229, 229, 0.37)" // TODO: remove bg when form-editor is updated to new design system
      borderRadius="6px"
      px="12px"
      py="6px"
      alignItems="center"
      justifyContent="space-between"
      gap="6px"
      className="preview-attachment"
      border="1px solid"
      borderColor={fileObj?.hasError ? 'error-100' : 'border-color'}
    >
      <Flex alignItems="center" gap="12px">
        <Icon
          as={getFileIcon(fileObj?.type)}
          w="44px"
          h="44px"
          borderRadius={'6px'}
        />
        <Flex flexDirection="column" maxW="180px">
          <Link
            href={fileUrl}
            target="_blank"
            outline="none"
            _focus={{ outline: 'none', boxShadow: 'none' }}
          >
            <TruncatedTextWithTooltip
              size="md-semi-bold"
              text={fileObj?.name}
              maxW="140px"
              cursor="pointer"
              toolTipVariant="default"
              color="secondary-900"
            />
          </Link>
          {fileObj?.size && (
            <Text color="secondary-700" fontSize="14px" fontWeight="semiBold">
              {formatBytes(fileObj?.size)}
            </Text>
          )}
        </Flex>
      </Flex>
      <Flex direction="row" alignItems="center" justifyContent="space-between">
        {fileObj?.isNonRemovable ? (
          <Tooltip
            label="This attachment cannot be removed"
            variant="ats-primary"
          >
            <Box>
              <Icon as={InfoCircle} />
            </Box>
          </Tooltip>
        ) : (
          <Icon
            as={X}
            width="16px"
            height="16px"
            cursor="pointer"
            onClick={onAttachmentRemove}
          />
        )}
      </Flex>
    </Flex>
  );
};
